import { useEffect } from "react";

/**
 * Per-page <title> and description, set on mount and restored on unmount.
 *
 * What this fixes: the browser tab, the bookmark name, the history entry, and
 * — the reason it isn't cosmetic — the title a screen reader announces after a
 * client-side route change. Today every route in the SPA inherits index.html's
 * one title, so navigating announces nothing new.
 *
 * What this does NOT fix: link unfurls in Slack, iMessage, WhatsApp, X,
 * Facebook or LinkedIn. Those crawlers fetch the raw HTML and never run JS, so
 * they only ever see index.html. That half is worker/public-pages.ts, which
 * rewrites the tags server-side for the public documents.
 *
 * No react-helmet-async: with no SSR it does exactly this, plus a dependency
 * and a provider.
 */
export function useDocumentMeta({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const tag = description
      ? document.querySelector<HTMLMetaElement>('meta[name="description"]')
      : null;
    const previousDescription = tag?.content;
    if (tag && description) tag.content = description;

    return () => {
      document.title = previousTitle;
      if (tag && previousDescription !== undefined) tag.content = previousDescription;
    };
  }, [title, description]);
}
